import type { JSX } from 'react';
import type { AgentId } from './protocol.ts';
import {
  Archive,
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  CornerUpLeft,
  Ellipsis,
  FileArchive,
  FileBraces,
  FileCode,
  FileImage,
  FileText,
  FlaskConicalOff,
  Folder,
  Folders,
  GitBranch,
  GitBranchPlus,
  GitCommitHorizontal,
  GitFork,
  GripVertical,
  House,
  ImagePlus,
  ListTodo,
  MessageSquare,
  Mic,
  Pause,
  Pencil,
  Play,
  Rocket,
  RotateCw,
  Search,
  SendHorizontal,
  Settings,
  Sparkles,
  Square,
  TextWrap,
  Trash2,
  Undo2,
  Upload,
  X,
  type LucideProps,
} from 'lucide-react';

/** Lucide's send glyph points right; messages in this UI go up into the thread. */
function SendUp({ style, ...props }: LucideProps): JSX.Element {
  return <SendHorizontal {...props} style={{ transform: 'rotate(-90deg)', ...style }} />;
}

/**
 * Named icons used by the UI. Components import from here rather than
 * lucide-react so the set stays small and the stroke stays consistent.
 */
const ICONS = {
  home: House,
  play: Play,
  pause: Pause,
  stop: Square,
  chat: MessageSquare,
  mic: Mic,
  image: ImagePlus,
  task: ListTodo,
  git: GitBranch,
  settings: Settings,
  more: Ellipsis,
  archive: Archive,
  trash: Trash2,
  deploy: Rocket,
  grip: GripVertical,
  projects: Folders,
  send: SendUp,
  sparkles: Sparkles,
  pencil: Pencil,
  check: Check,
  close: X,
  commit: GitCommitHorizontal,
  push: Upload,
  branchPlus: GitBranchPlus,
  parallel: GitFork,
  folder: Folder,
  file: FileText,
  flaskOff: FlaskConicalOff,
  fileCode: FileCode,
  fileData: FileBraces,
  fileImage: FileImage,
  fileBinary: FileArchive,
  chevronRight: ChevronRight,
  back: ArrowLeft,
  up: CornerUpLeft,
  refresh: RotateCw,
  copy: Copy,
  wrap: TextWrap,
  search: Search,
  undo: Undo2,
} as const;

export type IconName = keyof typeof ICONS;

export function Icon({
  name,
  size = 20,
  strokeWidth = 2,
  ...props
}: { name: IconName } & LucideProps): JSX.Element {
  const Cmp = ICONS[name];
  return <Cmp size={size} strokeWidth={strokeWidth} aria-hidden="true" {...props} />;
}

/**
 * Brand marks for the agent backends. Filled, so they sit in a FAB.
 * Claude and Cursor are Simple Icons; Grok is the official logomark from
 * https://x.ai/legal/brand-guidelines; OpenCode is the square mark from
 * https://opencode.ai/favicon.svg; OpenAI uses the official monoblossom asset.
 */
export function AgentLogo({
  agent,
  size = 20,
}: {
  agent: AgentId;
  size?: number;
}): JSX.Element {
  if (agent === 'claude') {
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
        <path
          fill="currentColor"
          d="M17.304 3.541h-3.672l6.696 16.918H24Zm-10.608 0L0 20.459h3.744l1.37-3.553h7.005l1.369 3.553h3.744L10.536 3.541Zm-.371 10.223L8.616 7.82l2.291 5.945Z"
        />
      </svg>
    );
  }
  if (agent === 'grok') {
    return (
      <svg viewBox="0 0 1024 1024" width={size} height={size} aria-hidden="true">
        <path
          fill="currentColor"
          d="M395.479 633.828L735.91 381.105C752.599 368.715 776.454 373.548 784.406 392.792C826.26 494.285 807.561 616.253 724.288 699.996C641.016 783.739 525.151 802.104 419.247 760.277L303.556 814.143C469.49 928.202 670.987 899.995 796.901 773.282C896.776 672.843 927.708 535.937 898.785 412.476L899.047 412.739C857.105 231.37 909.358 158.874 1016.4 10.6326C1018.93 7.11771 1021.47 3.60279 1024 0L883.144 141.651V141.212L395.392 633.916"
        />
        <path
          fill="currentColor"
          d="M325.226 695.251C206.128 580.84 226.662 403.776 328.285 301.668C403.431 226.097 526.549 195.254 634.026 240.596L749.454 186.994C728.657 171.88 702.007 155.623 671.424 144.2C533.19 86.9942 367.693 115.465 255.323 228.382C147.234 337.081 113.244 504.215 171.613 646.833C215.216 753.423 143.739 828.818 71.7385 904.916C46.2237 931.893 20.6216 958.87 0 987.429L325.139 695.339"
        />
      </svg>
    );
  }
  if (agent === 'opencode') {
    return (
      <svg viewBox="0 0 512 512" width={size} height={size} aria-hidden="true">
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M384 416H128V96H384V416ZM320 160H192V352H320V160Z"
        />
      </svg>
    );
  }
  if (agent === 'openai') {
    return <img src="/openai-logo.svg" width={size} height={size} alt="" aria-hidden="true" />;
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        fill="currentColor"
        d="M11.503.131L1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23"
      />
    </svg>
  );
}
